import { Box, Button, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { CloudDownload, CloudUpload, KeyboardBackspace } from '@mui/icons-material';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { IGpsrSkuItem, IGpsrTypesItem, TGpsrRegulationsItem } from 'redux/reducers/gpsr/types';
import { useLocation, useNavigate, useParams } from 'react-router';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import {
	attachSkusRequest,
	deleteSkuRequest,
	downloadGpsrDocRequest,
	getGpsrSkusRequest,
	getGpsrTechFileRequest,
	updateGpsrDescription,
	uploadGpsrDocRequest,
	uploadGpsrTechFileRequest,
} from 'redux/reducers/gpsr/reducer';
import {
	getGpsrDownloadDocIsLoad,
	getGpsrGenerateDocIsLoad,
	getGpsrGetTechFileIsLoad,
	getGpsrSkus,
	getGpsrSkusIsLoad,
	getGpsrUploadDocIsLoad,
	getGpsrUploadTechFileIsLoad,
} from 'redux/reducers/gpsr/selectors';
import { DeleteDialog } from 'ui/DeleteDialog';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { PATHS } from 'routes/constants';
import { VisuallyHiddenInput } from 'ui/VisuallyHiddenInput';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { gpsrSkuTableStructure } from './tableStructure/gpsrSkuTableStructure';
import { AddNewSkusDialog } from './AddNewSkusDialog';
import { EditSkuDialog } from './EditSkuDialog';
import { gpsrTypesTableStructure } from './tableStructure/gpsrTypesTableStructure';
import { EditDescriptionDialog } from './EditDescriptionDialog';
import { gpsrRegulationsTableStructure } from './tableStructure/gpsrRegulationsTableStructure';
import { GenerateGpsrDeclarationDialog } from './GenerateGpsrDeclarationDialog';

export const GpsrSku: FC = () => {
	const params = useParams<{ productTypeId?: string }>();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const generateDeclarationIsLoad = useSelector(getGpsrGenerateDocIsLoad);
	const uploadDeclarationIsLoad = useSelector(getGpsrUploadDocIsLoad);
	const downloadDeclarationIsLoad = useSelector(getGpsrDownloadDocIsLoad);
	const savedUrl = useSelector(getSavedUrl);
	const uploadTechFileIsLoad = useSelector(getGpsrUploadTechFileIsLoad);
	const getTechFileIsLoad = useSelector(getGpsrGetTechFileIsLoad);

	const [selectedSku, setSelectedSku] = useState<number | null>(null);

	const [openAddNewSkusDialog, setOpenAddNewSkusDialog] = useState(false);
	const handleOpenAddNewSkusDialog = () => {
		setOpenAddNewSkusDialog(true);
	};
	const handleCloseAddNewSkusDialog = () => {
		setOpenAddNewSkusDialog(false);
	};

	const [openEditSkuDialog, setOpenEditSkuDialog] = useState(false);
	const handleOpenEditSkuDialog = () => {
		setOpenEditSkuDialog(true);
	};
	const handleCloseEditSkuDialog = () => {
		setOpenEditSkuDialog(false);
	};
	const handleEditSkuDialog = (skuText: string) => {
		if (!params.productTypeId) return;
		dispatch(
			attachSkusRequest({
				productTypeId: +params.productTypeId,
				body: {
					skusTxt: skuText,
				},
			}),
		);
		handleCloseEditSkuDialog();
	};

	const [openGenerateDeclarationDialog, setOpenGenerateDeclarationDialog] = useState(false);
	const handleOpenGenerateDeclarationDialog = () => {
		setOpenGenerateDeclarationDialog(true);
	};
	const handleCloseGenerateDeclarationDialog = () => {
		setOpenGenerateDeclarationDialog(false);
	};

	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const handleOpenDeleteDialog = () => {
		setOpenDeleteDialog(true);
	};
	const handleCloseDeleteDialog = () => {
		setOpenDeleteDialog(false);
	};
	const handleDeleteDialog = () => {
		if (!params.productTypeId) return;
		if (!selectedSku) return;
		dispatch(
			deleteSkuRequest({
				productTypeId: +params.productTypeId,
				skuId: selectedSku,
			}),
		);
		handleCloseDeleteDialog();
	};

	const gpsrSku = useSelector(getGpsrSkus);
	const gpsrSkuIsLoad = useSelector(getGpsrSkusIsLoad);

	const items = gpsrSku?.skus;

	const gpsrItem = gpsrSku?.product_type;

	const gpsrItems = gpsrItem
		? {
				data: [gpsrItem],
				totalCount: 1,
				pageNumber: 1,
				pageSize: 1,
			}
		: null;

	useEffect(() => {
		if (!params.productTypeId) return;
		dispatch(
			getGpsrSkusRequest({
				productTypeId: +params.productTypeId,
				body: {
					pageNumber: page ? +page : 1,
					pageSize: PAGE_SIZE,
					searchString: String(navigateParams?.search ?? ''),
				},
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, params.productTypeId, search, page]);

	const handleReplayHistory = () => {
		// window.history.back();
		if (savedUrl) {
			if (savedUrl.includes(PATHS.GPSR_SERVICE_PAGE)) navigate(savedUrl);
			else navigate(PATHS.GPSR_SERVICE_PAGE);
		}
	};

	const handleSkuActions = (skuId: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Edit') {
			setSelectedSku(skuId);
			handleOpenEditSkuDialog();
		}
		if (typeOfDialog === 'Delete') {
			setSelectedSku(skuId);
			handleOpenDeleteDialog();
		}
	};

	const handleDownloadDoc = () => {
		if (!params.productTypeId) return;
		dispatch(
			downloadGpsrDocRequest({
				productTypeId: +params.productTypeId,
			}),
		);
	};

	const handleUploadDoc = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!params.productTypeId) return;
		if (!e.target.files) return;
		const formData = new FormData();
		formData.append('File', e.target.files[0]);

		dispatch(
			uploadGpsrDocRequest({
				productTypeId: +params.productTypeId,
				file: formData,
			}),
		);
	};

	const [openEditDescriptionDialog, setOpenEditDescriptionDialog] = useState(false);
	const handleOpenEditDescriptionDialog = () => {
		setOpenEditDescriptionDialog(true);
	};
	const handleCloseEditDescriptionDialog = () => {
		setOpenEditDescriptionDialog(false);
	};
	const handleEditDescriptionDialog = (description: string) => {
		if (!params.productTypeId) return;
		dispatch(
			updateGpsrDescription({
				productTypeId: +params.productTypeId,
				body: {
					productTypeDescription: description,
				},
			}),
		);
		handleCloseEditDescriptionDialog();
	};

	const handleGpsrActions = (productTypeId: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Edit Description') {
			setSelectedSku(productTypeId);
			handleOpenEditDescriptionDialog();
		}
		if (typeOfDialog === 'Public Page') {
			window.open(gpsrSku?.product_type?.doCLink ?? '', '_blank');
		}
	};

	const handleDownloadTechFile = () => {
		if (!params.productTypeId) return;
		dispatch(
			getGpsrTechFileRequest({
				productTypeId: +params.productTypeId,
			}),
		);
	};

	const handleUploadTechFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!params.productTypeId) return;
		if (!e.target.files) return;
		const formData = new FormData();
		formData.append('File', e.target.files[0]);
		dispatch(
			uploadGpsrTechFileRequest({
				productTypeId: +params.productTypeId,
				file: formData,
			}),
		);
	};
	return (
		<Box>
			<EasTableWithoutFilter<IGpsrTypesItem>
				tableStructure={gpsrTypesTableStructure as TTableStructure<keyof IGpsrTypesItem>[]}
				items={gpsrItems}
				itemsIsLoading={gpsrSkuIsLoad}
				skeletonRows={[1]}
				handleAction={handleGpsrActions}
				handleLink={() => {}}
				highlightedRow={null}
				showButtons={[
					{
						'Public Page': gpsrSku?.product_type.status !== 'No Declaration',
					},
				]}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Box
						sx={{
							backgroundColor: 'primary.light',
							display: 'flex',
							justifyContent: 'start',
							alignItems: 'center',
							gap: '10px',
							cursor: 'pointer',
						}}
						onClick={handleReplayHistory}
					>
						<IconButton
							color="primary"
							sx={{
								p: '0',
							}}
						>
							<KeyboardBackspace />
						</IconButton>
						<Typography
							sx={{
								fontSize: '.9rem',
								color: 'primary.main',
								fontWeight: 'bold',
							}}
						>
							GPSR Service
						</Typography>
					</Box>

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: 1,
						}}
					>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							color="primary"
							size="small"
							tabIndex={-1}
							startIcon={<CloudUpload />}
							disabled={
								generateDeclarationIsLoad ||
								downloadDeclarationIsLoad ||
								uploadDeclarationIsLoad ||
								uploadTechFileIsLoad ||
								getTechFileIsLoad
							}
						>
							Upload Technical File (PDF)
							{uploadTechFileIsLoad && (
								<CircularProgress
									size={24}
									sx={{
										color: '#004366',
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}
								/>
							)}
							<VisuallyHiddenInput type="file" onChange={handleUploadTechFile} multiple />
						</Button>
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={handleDownloadTechFile}
							startIcon={<CloudDownload />}
							disabled={
								generateDeclarationIsLoad ||
								downloadDeclarationIsLoad ||
								uploadDeclarationIsLoad ||
								uploadTechFileIsLoad ||
								getTechFileIsLoad
							}
						>
							Download Technical File (PDF)
							{getTechFileIsLoad && (
								<CircularProgress
									size={24}
									sx={{
										color: '#004366',
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}
								/>
							)}
						</Button>
						<Button
							variant="contained"
							color="primary"
							size="small"
							disabled={
								generateDeclarationIsLoad ||
								downloadDeclarationIsLoad ||
								uploadDeclarationIsLoad ||
								uploadTechFileIsLoad ||
								getTechFileIsLoad
							}
							startIcon={<CloudDownload />}
							onClick={handleOpenGenerateDeclarationDialog}
						>
							Generate and Download new Declaration (PDF)
							{generateDeclarationIsLoad && (
								<CircularProgress
									size={24}
									sx={{
										color: '#004366',
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}
								/>
							)}
						</Button>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							color="primary"
							size="small"
							tabIndex={-1}
							startIcon={<CloudUpload />}
							disabled={
								generateDeclarationIsLoad ||
								downloadDeclarationIsLoad ||
								uploadDeclarationIsLoad ||
								uploadTechFileIsLoad ||
								getTechFileIsLoad
							}
						>
							Upload Signed Declaration (PDF)
							{uploadDeclarationIsLoad && (
								<CircularProgress
									size={24}
									sx={{
										color: '#004366',
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}
								/>
							)}
							<VisuallyHiddenInput type="file" onChange={handleUploadDoc} multiple />
						</Button>

						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={handleDownloadDoc}
							startIcon={<CloudDownload />}
							disabled={
								generateDeclarationIsLoad ||
								downloadDeclarationIsLoad ||
								uploadDeclarationIsLoad ||
								uploadTechFileIsLoad ||
								getTechFileIsLoad
							}
						>
							Download Signed Declaration (PDF)
							{downloadDeclarationIsLoad && (
								<CircularProgress
									size={24}
									sx={{
										color: '#004366',
										position: 'absolute',
										top: '50%',
										left: '50%',
										marginTop: '-12px',
										marginLeft: '-12px',
									}}
								/>
							)}
						</Button>
					</Box>
				</Box>
			</EasTableWithoutFilter>

			{!!items && (
				<Box sx={{ margin: '0px 10px 0 10px' }}>
					<Grid container spacing={1.25}>
						<Grid item xs={12} lg={6}>
							<EasTableWithFilter<IGpsrSkuItem>
								tableStructure={gpsrSkuTableStructure}
								items={items}
								itemsIsLoading={gpsrSkuIsLoad}
								handleAction={handleSkuActions}
								handleLink={() => {}}
								highlightedRow={null}
								name="SKU"
								filterChildren={
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={handleOpenAddNewSkusDialog}
									>
										Add new SKU
									</Button>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<EasTableWithFilter<TGpsrRegulationsItem>
								tableStructure={gpsrRegulationsTableStructure}
								items={{ data: gpsrItems?.data[0]?.regulations ?? [] }}
								itemsIsLoading={gpsrSkuIsLoad}
								// skeletonRows={[1]}
								handleAction={() => {}}
								handleLink={() => {}}
								highlightedRow={null}
								name="Regulation"
								filterChildren={
									<Typography
										sx={{
											fontSize: '.9rem',
											color: 'primary.main',
											fontWeight: 'bold',
											padding: '4px',
										}}
									>
										Regulations
									</Typography>
								}
							/>
						</Grid>
					</Grid>
				</Box>
			)}
			<AddNewSkusDialog
				open={openAddNewSkusDialog}
				setClose={handleCloseAddNewSkusDialog}
				productTypeId={+(params.productTypeId as string)}
				// handleAdd={handleAddNewSkusDialog}
			/>
			<EditSkuDialog
				open={openEditSkuDialog}
				setClose={handleCloseEditSkuDialog}
				skuText={items?.data.find((item) => item.id === selectedSku)?.name ?? ''}
				handleEdit={handleEditSkuDialog}
			/>
			<DeleteDialog
				open={openDeleteDialog}
				setClose={handleCloseDeleteDialog}
				handleDelete={handleDeleteDialog}
				text="Are you sure you want to delete this SKU?"
			/>
			<EditDescriptionDialog
				open={openEditDescriptionDialog}
				setClose={handleCloseEditDescriptionDialog}
				handleEdit={handleEditDescriptionDialog}
				descriptionText={gpsrItem?.description ?? ''}
			/>
			<GenerateGpsrDeclarationDialog
				open={openGenerateDeclarationDialog}
				setClose={handleCloseGenerateDeclarationDialog}
				communicationPhoneNumber={gpsrItem?.communicationPhone ?? ''}
				communicationEmail={gpsrItem?.communicationEmail ?? ''}
				testingInformation={gpsrItem?.testingInformation ?? ''}
			/>
		</Box>
	);
};
